import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import useEmergenTTS from '../../hooks/useEmergenTTS';
import { useLanguage } from '../../contexts/LanguageContext';



const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimpleVoiceTaskCreator = ({ onTaskCreated, onClose }) => {
  // Open the modal in 'listening' mode directly — no intro screen, no spoken
  // greeting, no "Start Voice" button. The user tapped a mic to dictate a
  // task; the mic should be open by the time the modal lands. The Web Speech
  // API auto-stops on silence so the user just talks and the result comes
  // back in ~1-1.5 s after they finish. TTS is still used for follow-up
  // questions where the user can't necessarily look at the screen.
  const [step, setStep] = useState('listening'); // 'speaking', 'listening', 'processing', 'followup', 'confirm', 'done'
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [taskPreview, setTaskPreview] = useState(null);
  const [currentMissingFields, setCurrentMissingFields] = useState([]);
  const [followupAttempts, setFollowupAttempts] = useState(0);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [lastQuestion, setLastQuestion] = useState('');
  
  const recognitionRef = useRef(null);
  const startListeningRef = useRef(null); // Ref to avoid temporal dead zone
  const createTaskRef = useRef(null); // Ref to avoid temporal dead zone with createTask

  // Language context for multi-language support
  const { t, getLanguageCode, getLanguageName } = useLanguage();

  // Smart category selection based on task content
  const smartSelectCategory = useCallback(async (taskContent) => {
    try {
      // Get existing categories
      const response = await axios.get(`${API}/categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const categories = response.data || [];
      
      if (categories.length === 0) {
        return ''; // No categories available
      }
      
      // Simple keyword-based category matching
      const content = taskContent.toLowerCase();
      
      // Define category keywords
      const categoryKeywords = {
        'work': ['work', 'meeting', 'project', 'office', 'client', 'business', 'report', 'email', 'call', 'presentation'],
        'personal': ['personal', 'home', 'family', 'friend', 'doctor', 'appointment', 'health', 'gym', 'exercise'],
        'shopping': ['shop', 'buy', 'purchase', 'store', 'grocery', 'market', 'mall'],
        'health': ['doctor', 'hospital', 'medication', 'exercise', 'gym', 'diet', 'health', 'medical'],
        'finance': ['bank', 'money', 'pay', 'bill', 'budget', 'investment', 'tax'],
        'education': ['study', 'learn', 'course', 'class', 'homework', 'exam', 'book', 'school']
      };
      
      // Find best matching category
      let bestMatch = '';
      let maxScore = 0;
      
      for (const category of categories) {
        const categoryName = category.name.toLowerCase();
        let score = 0;
        
        // Check if category name appears in content
        if (content.includes(categoryName)) {
          score += 10;
        }
        
        // Check keyword matches
        const keywords = categoryKeywords[categoryName] || [];
        for (const keyword of keywords) {
          if (content.includes(keyword)) {
            score += 5;
          }
        }
        
        if (score > maxScore) {
          maxScore = score;
          bestMatch = category.name;
        }
      }
      
      // If no good match found, return the first category or empty
      return bestMatch || (categories.length > 0 ? categories[0].name : '');
      
    } catch (error) {
      console.error('Error selecting smart category:', error);
      return ''; // Fallback to no category
    }
  }, []);

  // DIRECT FIELD TRANSLATION MAPPING (as backup solution)
  const getFieldTranslations = useCallback(() => {
    const currentLang = getLanguageCode ? getLanguageCode() : "en";
    const fieldTranslations = {
      'en': {
        title: 'title',
        due_date: 'due date',
        due_time: 'due time',
        time: 'time',
        priority: 'priority',
        category: 'category',
        location: 'location',
        contact: 'contact',
        notes: 'notes',
        description: 'description'
      },
      'es': {
        title: 'título',
        due_date: 'fecha de vencimiento',
        due_time: 'hora de vencimiento',
        time: 'hora',
        priority: 'prioridad', 
        category: 'categoría',
        location: 'ubicación',
        contact: 'contacto',
        notes: 'notas',
        description: 'descripción'
      },
      'fr': {
        title: 'titre',
        due_date: 'date d\'échéance',
        due_time: 'heure d\'échéance', 
        time: 'heure',
        priority: 'priorité',
        category: 'catégorie',
        location: 'lieu',
        contact: 'contact',
        notes: 'notes',
        description: 'description'
      }
    };
    return fieldTranslations[currentLang] || fieldTranslations['en'];
  }, [getLanguageCode]);

  // SIMPLIFIED FIELD TRANSLATOR WITH EXPLICIT DEBUGGING
  const translateField = useCallback((fieldKey) => {
    const currentLang = getLanguageCode ? getLanguageCode() : "en";
    console.log('🐛 TRANSLATE FIELD DEBUG:', { fieldKey, currentLang });
    
    const fieldTranslations = {
      'en': {
        title: 'title', due_date: 'due date', due_time: 'due time', time: 'time',
        priority: 'priority', category: 'category', location: 'location',
        contact: 'contact', notes: 'notes', description: 'description'
      },
      'es': {
        title: 'título', due_date: 'fecha de vencimiento', due_time: 'hora de vencimiento', time: 'hora',
        priority: 'prioridad', category: 'categoría', location: 'ubicación',
        contact: 'contacto', notes: 'notas', description: 'descripción'
      },
      'fr': {
        title: 'titre', due_date: 'date d\'échéance', due_time: 'heure d\'échéance', time: 'heure',
        priority: 'priorité', category: 'catégorie', location: 'lieu',
        contact: 'contact', notes: 'notes', description: 'description'
      }
    };
    
    const langMap = fieldTranslations[currentLang] || fieldTranslations['en'];
    const translated = langMap[fieldKey] || fieldKey.replace(/_/g, ' ');
    
    console.log('🐛 FIELD TRANSLATION:', fieldKey, '→', translated, 'Language:', currentLang);
    return translated;
  }, [getLanguageCode]);

  const translateFields = useCallback((fieldKeys) => {
    console.log('🐛 TRANSLATE FIELDS CALLED:', fieldKeys);
    const result = fieldKeys.map(fieldKey => translateField(fieldKey));
    console.log('🐛 TRANSLATE FIELDS RESULT:', result);
    return result;
  }, [translateField]);

  // High-quality Emergent TTS for AI voice responses
  const {
    speakConversation,
    isLoading: isSpeaking,
    isSpeaking: isCurrentlySpeaking,
    error: ttsError,
    stopSpeaking,
    availableVoices
  } = useEmergenTTS();

  // Enhanced AI voice function using Emergent TTS (replaces Web Speech API)
  const speakText = useCallback(async (text) => {
    if (!text || !text.trim()) {
      console.log('⚠️ Empty text provided to speakText');
      return false;
    }

    const languageCode = getLanguageCode ? getLanguageCode() : 'en';
    const languageName = getLanguageName ? getLanguageName() : 'English';
    console.log('🎤 Speaking with Emergent TTS in', languageName, 'code:', languageCode, ':', text.substring(0, 50) + '...');
    
    try {
      // Use high-quality neural TTS via Emergent integration with language support
      const success = await speakConversation(text, 'nova', languageCode); // Nova = young female voice
      
      if (success) {
        console.log('✅ Emergent TTS completed successfully');
        return true;
      } else {
        console.log('⚠️ Emergent TTS failed, no fallback needed');
        return false;
      }
    } catch (error) {
      console.error('❌ Emergent TTS error:', error);
      return false;
    }
  }, [speakConversation, getLanguageCode, getLanguageName]);

  // Cleanup voice operations when component unmounts or modal closes
  useEffect(() => {
    return () => {
      console.log('🔇 VoiceTaskCreator: Stopping all voice operations on cleanup');
      try {
        // Stop any ongoing TTS
        if (stopSpeaking) {
          stopSpeaking();
        }
        // Stop speech recognition if active
        if (recognitionRef.current) {
          recognitionRef.current.abort();
        }
        // Cancel any speech synthesis
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      } catch (error) {
        console.log('Error stopping voice operations on cleanup:', error);
      }
    };
  }, [stopSpeaking]);

  // Generate contextual follow-up questions
  const generateFollowupQuestion = useCallback((missingField, currentFields) => {
    const taskTitle = currentFields?.title || t('thisTask');
    
    const questions = {
      'due_date': t('questionWhenDue', { taskTitle }),
      'due_time': t('questionWhatTime', { taskTitle }),
      'time': t('questionWhatTime', { taskTitle }),
      'priority': t('questionPriority', { taskTitle }),
      'location': t('questionLocation', { taskTitle }),
      'category': t('questionCategory', { taskTitle }),
      'contact': t('questionContact', { taskTitle }),
      'notes': t('questionNotes', { taskTitle }),
      'description': t('questionDescription', { taskTitle })
    };
    
    return questions[missingField] || t('questionGeneric', { 
      field: missingField.replace(/_/g, ' '), 
      taskTitle 
    });
  }, [t]);

  // Process initial input and extract task data
  const processInitialInput = useCallback(async (userInput) => {
    try {
      // Get user's local date and time
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const localDate = `${year}-${month}-${day}`;
      const localTime = now.toTimeString().split(' ')[0].substring(0, 5);
      
      // Get timezone offset in minutes (negative of getTimezoneOffset)
      const timezoneOffset = -now.getTimezoneOffset();
      
      console.log('📅 Voice Task - User local context:', {
        localDate,
        localTime,
        timezoneOffset: `${timezoneOffset} minutes (UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset / 60})`
      });
      
      const response = await axios.post(`${API}/ai/parse-task`, {
        user_input: userInput,
        context: {
          current_date: localDate,  // User's LOCAL date
          current_time: localTime,  // User's LOCAL time
          timezone_offset: timezoneOffset  // User's timezone offset in minutes
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const aiData = response.data.data;
        return { handled: true, continue_flow: false, result: aiData };
      } else {
        return { handled: false, continue_flow: true };
      }
    } catch (error) {
      console.error('❌ Process initial input error:', error);
      return { handled: false, continue_flow: true };
    }
  }, []);

  // ENHANCED CONVERSATIONAL AI HANDLER WITH SMART CONTEXT
  const handleConversationalAI = useCallback(async (text, intent = 'general') => {
    try {
      console.log('🧠 ENHANCED AI Processing:', text, 'Intent:', intent);
      
      // Enhanced conversation context with task state
      const enhancedContext = {
        conversation_history: conversationHistory,
        current_task_state: taskPreview,
        missing_fields: currentMissingFields,
        available_contacts: [],
        available_categories: ['Work', 'Personal', 'Health', 'Education', 'Shopping', 'Family', 'Finance', 'Travel'],
        current_step: step,
        user_intent: intent,
        
        // SMART CONTEXT: What the AI should understand
        smart_patterns: {
          create_commands: ['create task', 'save task', 'make task', 'proceed', 'go ahead', 'create with what you have', 'keep information and create'],
          skip_commands: ['skip', 'not needed', 'ignore', 'no contact', 'without contact', 'continue without'],
          info_requests: ['what do you need', 'which information', 'what are you missing', 'tell me what you need'],
          confirmation: ['yes', 'correct', 'right', 'exactly', 'that works', 'sounds good'],
          rejection: ['no', 'wrong', 'incorrect', 'try again', 'not right']
        }
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/conversational`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: text,
          conversation_context: enhancedContext,
          language: getLanguageName ? getLanguageName() : "English",
          language_code: getLanguageCode ? getLanguageCode() : "en",
          intent: intent,
          // ENHANCED: Tell the AI what responses are expected
          expected_response_types: ['task_creation', 'field_update', 'clarification', 'confirmation', 'information_request']
        })
      });

      const data = await response.json();
      
      if (data.success && data.response) {
        console.log('🎯 SMART AI Response:', data.response);
        
        // ENHANCED: Check if AI understood the intent properly
        if (data.detected_intent && data.detected_intent !== 'general_fallback') {
          console.log('✅ AI understood intent:', data.detected_intent);
          
          // AI understood the intent - use its response
          await speakText(data.response);
          
          // SMART ACTION HANDLING based on AI response
          if (data.detected_intent === 'create_task_request') {
            if (taskPreview?.title) {
              setStep('confirm');
            } else {
              await speakText(t('needTitleAtLeast'));
              setStep('followup');
            }
            return { handled: true, continue_flow: false };
          }
          
          if (data.detected_intent === 'skip_field' || data.detected_intent === 'no_contact_needed') {
            const nextFields = currentMissingFields.slice(1);
            setCurrentMissingFields(nextFields);
            
            if (nextFields.length > 0) {
              const nextQuestion = generateFollowupQuestion(nextFields[0], taskPreview);
              await speakText(nextQuestion);
              setStep('followup');
            } else {
              await speakText(t('perfectEnoughInfo'));
              setStep('confirm');
            }
            return { handled: true, continue_flow: false };
          }
          
          if (data.detected_intent === 'information_request') {
            if (currentMissingFields.length > 0) {
              const taskSchema = {
                due_date: { description: "date" },
                due_time: { description: "time" },
                priority: { description: "priority" },
                category: { description: "category" },
                location: { description: "location" },
                contact: { description: "contact" },
                notes: { description: "notes" }
              };
              const remaining = currentMissingFields.map(f => 
                taskSchema[f]?.description || f.replace(/_/g, ' ')
              ).join(', ');
              const nextField = taskSchema[currentMissingFields[0]]?.description || currentMissingFields[0];
              await speakText(t('iStillNeed', { remaining, nextField }));
            }
            return { handled: true, continue_flow: false };
          }
          
          return { handled: true, continue_flow: true };
        }
      }
      
      console.log('⚠️ AI fallback - using pattern matching');
      return { handled: false, continue_flow: true };
      
    } catch (error) {
      console.log('❌ Conversational AI Error:', error);
      return { handled: false, continue_flow: true };
    }
  }, [conversationHistory, taskPreview, currentMissingFields, step, getLanguageName, getLanguageCode, t]);

  // ENHANCED INTELLIGENT CONVERSATION HANDLER
  const handleSmartResponse = useCallback(async (text) => {
    // First, try the conversational AI for intelligent handling
    const conversationalResult = await handleConversationalAI(text);
    
    if (conversationalResult.handled && !conversationalResult.continue_flow) {
      // Conversational AI handled this completely
      return true;
    }
    
    // Enhanced natural language understanding
    const lowerText = text.toLowerCase().trim();
    
    console.log('🧠 Smart Response Analysis:', lowerText);

    // === INTELLIGENT COMMAND RECOGNITION ===
    
    // 1. ADVANCED "CREATE TASK NOW" PATTERNS
    const createTaskPatterns = [
      // Direct commands
      /create (the |this )?task/i,
      /make (the |this )?task/i,
      /save (the |this )?task/i,
      /go ahead and create/i,
      /just create/i,
      /create it now/i,
      /make it now/i,
      
      // "With what you have" patterns
      /create with what you have/i,
      /use what you have/i,
      /keep what you have and create/i,
      /save with current info/i,
      /create anyway/i,
      /proceed anyway/i,
      
      // "That's enough" patterns
      /that'?s enough/i,
      /enough information/i,
      /sufficient/i,
      /done asking/i,
      /stop asking/i,
      /no more questions/i,
      
      // Skip remaining patterns
      /skip (the )?rest/i,
      /ignore missing/i,
      /don'?t need anything else/i,
      /create incomplete/i,
      /create partial/i,
      
      // Natural conversational patterns
      /no,? ?(just )?keep (what you have|the information|it) and create/i,
      /no,? ?(just )?create (the task|it) with what you have/i,
      /forget the rest,? ?(just )?create/i,
      /i'?m done,? ?(just )?create/i
    ];

    const isCreateCommand = createTaskPatterns.some(pattern => pattern.test(text));
    
    if (isCreateCommand) {
      console.log('🎯 DETECTED: Create task command');
      
      if (taskPreview && taskPreview.title) {
        let confirmationMessage = t('perfectCreateTaskWith', { taskTitle: taskPreview.title });
        
        if (taskPreview.due_date) {
          confirmationMessage += `, due ${taskPreview.due_date}`;
        }
        if (taskPreview.priority) {
          confirmationMessage += `, ${taskPreview.priority.toLowerCase()} priority`;
        }
        
        await speakText(confirmationMessage);
        setStep('confirm');
        return true;
      } else {
        await speakText(t('needTitleAtLeast'));
        setStep('followup');
        return true;
      }
    }

    // 2. ADVANCED "NO CONTACT" PATTERNS
    const noContactPatterns = [
      /no contact/i,
      /don'?t (want|need) (a )?contact/i,
      /no,? ?don'?t (add|link|use) (a |any )?contact/i,
      /skip (the )?contact/i,
      /ignore (the )?contact/i,
      /no person/i,
      /without (a )?contact/i,
      /continue without/i,
      /i don'?t want (to add|any) contact/i
    ];

    const isNoContact = noContactPatterns.some(pattern => pattern.test(text));
    
    if (isNoContact && currentMissingFields.includes('contact')) {
      console.log('🎯 DETECTED: No contact wanted');
      
      // Remove contact from missing fields
      const updatedMissing = currentMissingFields.filter(f => f !== 'contact');
      setCurrentMissingFields(updatedMissing);
      
      if (updatedMissing.length > 0) {
        const nextQuestion = generateFollowupQuestion(updatedMissing[0], taskPreview);
        await speakText(t('okaySkipping', { nextQuestion }));
        setStep('followup');
      } else {
        await speakText(t('perfectEnoughInfo'));
        setStep('confirm');
      }
      return true;
    }

    // 3. INTELLIGENT "WHAT DO YOU NEED" RESPONSES
    const infoRequestPatterns = [
      /what (do you|information do you) need/i,
      /what are you missing/i,
      /what else do you need/i,
      /what'?s (still )?missing/i,
      /what (other )?information/i,
      /tell me what you need/i,
      /list what you need/i,
      /what'?s left/i,
      /what remains/i,
      /what still needed/i
    ];

    const isInfoRequest = infoRequestPatterns.some(pattern => pattern.test(text));
    
    if (isInfoRequest) {
      console.log('🎯 DETECTED: Information request');
      
      if (currentMissingFields.length > 0) {
        console.log('🐛 VOICE RESPONSE DEBUG - iStillNeed:');
        console.log('  - currentMissingFields:', currentMissingFields);
        const remaining = translateFields(currentMissingFields).join(', ');
        const nextField = translateField(currentMissingFields[0]);
        console.log('  - remaining (translated):', remaining);
        console.log('  - nextField (translated):', nextField);
        const finalMessage = t('iStillNeed', { remaining, nextField });
        console.log('  - finalMessage:', finalMessage);
        await speakText(finalMessage);
        
        const question = generateFollowupQuestion(currentMissingFields[0], taskPreview);
        await speakText(question);
      } else {
        await speakText(t('haveEverythingReady'));
      }
      setStep('followup');
      return true;
    }

    // 4. ENHANCED FIELD SKIP PATTERNS
    const skipPatterns = [
      /skip/i,
      /not needed/i,
      /don'?t need/i,
      /not required/i,
      /leave (it )?blank/i,
      /ignore (this|that)/i,
      /no,? ?(skip|ignore|leave|forget) (this|that|it)/i,
      /i don'?t want (to add|to set)/i,
      /not important/i
    ];

    const isSkip = skipPatterns.some(pattern => pattern.test(text));
    
    if (isSkip) {
      console.log('🎯 DETECTED: Skip current field');
      
      const nextFields = currentMissingFields.slice(1);
      setCurrentMissingFields(nextFields);
      
      if (nextFields.length > 0) {
        const nextQuestion = generateFollowupQuestion(nextFields[0], taskPreview);
        await speakText(t('okaySkipping', { nextQuestion }));
        setStep('followup');
      } else {
        await speakText(t('perfectEnoughInfo'));
        setStep('confirm');
      }
      return true;
    }

    // 5. SMART CONFIRMATION PATTERNS
    const confirmationPatterns = [
      /^(yes|yeah|yep|sure|ok|okay|correct|right|exactly|that'?s right)$/i,
      /go ahead/i,
      /sounds good/i,
      /looks good/i,
      /perfect/i
    ];

    const isConfirmation = confirmationPatterns.some(pattern => pattern.test(text));
    
    if (isConfirmation && taskPreview?.contact_name && currentMissingFields.includes('contact')) {
      console.log('🎯 DETECTED: Contact confirmation');
      
      const nextFields = currentMissingFields.filter(f => f !== 'contact');
      setCurrentMissingFields(nextFields);
      
      if (nextFields.length > 0) {
        const nextQuestion = generateFollowupQuestion(nextFields[0], taskPreview);
        await speakText(t('greatUsing', { contactName: taskPreview.contact_name, nextQuestion }));
        setStep('followup');
      } else {
        await speakText(t('perfectReadyToCreate'));
        setStep('confirm');
      }
      return true;
    }

    // 6. ENHANCED NEGATIVE RESPONSES
    const negativePatterns = [
      /^no$/i,
      /^nope$/i,
      /^not right$/i,
      /^incorrect$/i,
      /^wrong$/i,
      /that'?s not right/i,
      /try again/i
    ];

    const isNegative = negativePatterns.some(pattern => pattern.test(text));
    
    if (isNegative) {
      console.log('🎯 DETECTED: Negative response');
      
      if (currentMissingFields.length > 0) {
        const currentField = currentMissingFields[0];
        const question = generateFollowupQuestion(currentField, taskPreview);
        await speakText(t('okayLetMeAskAgain', { question }));
        setStep('followup');
        return true;
      }
    }

    // System knowledge - Task schema and options
    const taskSchema = {
      title: { required: true, description: "What you need to do" },
      due_date: { required: true, description: "When it's due", examples: ["tomorrow", "next Monday", "December 15th"] },
      due_time: { required: true, description: "What time", examples: ["2 PM", "morning", "afternoon"] },
      priority: { required: true, options: ["High", "Medium", "Low"], description: "How important it is" },
      category: { required: true, options: ["Work", "Personal", "Health", "Education", "Shopping", "Family", "Finance", "Travel"], description: "Type of task" },
      location: { required: false, description: "Where it happens", examples: ["office", "home", "gym"] },
      contact: { required: false, description: "Person involved", examples: ["John Smith", "Dr. Johnson"] },
      notes: { required: false, description: "Additional details or reminders" }
    };

    // 2. CONTACT DATABASE QUERIES
    if (lowerText.includes('do i have a contact') || lowerText.includes('is there a contact') ||
        lowerText.includes('do you know') || lowerText.includes('find contact') ||
        lowerText.includes('search contact') || lowerText.includes('contact named') ||
        lowerText.includes('contact called')) {
      
      try {
        // Extract the contact name from the query
        const nameMatch = text.match(/(?:contact|person|named|called)\s+([A-Za-z\s]+?)(?:\?|$|,)/i);
        if (nameMatch) {
          const contactName = nameMatch[1].trim();
          
          // Query the contacts API
          const response = await axios.get(`${API}/contacts`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          const contacts = response.data;
          const foundContact = contacts.find(contact => 
            contact.name.toLowerCase().includes(contactName.toLowerCase()) ||
            contactName.toLowerCase().includes(contact.name.toLowerCase())
          );
          
          if (foundContact) {
            await speakText(t('foundContactConfirm', { contactName: foundContact.name }));
            // Set the contact for potential use
            setTaskPreview(prev => ({ ...prev, contact_name: foundContact.name }));
          } else {
            await speakText(t('contactNotFound', { contactName }));
          }
        } else {
          await speakText(t('didntCatchContactName'));
        }
        
        setStep('followup');
        return true;
      } catch (error) {
        console.error('Contact query error:', error);
        await speakText(t('troubleCheckingContacts'));
        setStep('followup');
        return true;
      }
    }

    // 3. REPETITION & CLARIFICATION REQUESTS
    if (lowerText.includes('what did you say') || lowerText.includes('repeat') || 
        lowerText.includes('what was the question') || lowerText.includes('say that again') ||
        lowerText.includes('i didn\'t hear') || lowerText.includes('come again') ||
        lowerText.includes('pardon') || lowerText.includes('excuse me')) {
      
      if (currentMissingFields.length > 0) {
        const currentField = currentMissingFields[0];
        const question = generateFollowupQuestion(currentField, taskPreview);
        await speakText(t('iAsked', { question }));
      } else {
        await speakText(t('askingAboutTaskDetails'));
      }
      setStep('followup');
      return true;
    }

    // 4. FIELD-SPECIFIC QUERIES AND RESPONSES
    if (lowerText.includes('what\'s my') || lowerText.includes('what is my') ||
        lowerText.includes('current') || lowerText.includes('so far')) {
      
      const filled = [];
      const missing = [];
      
      Object.keys(taskSchema).forEach(field => {
        if (taskPreview && taskPreview[field]) {
          filled.push(`${taskSchema[field].description}: ${taskPreview[field]}`);
        } else if (currentMissingFields.includes(field)) {
          missing.push(taskSchema[field].description);
        }
      });
      
      let response = "";
      if (filled.length > 0) {
        response += `Here's what I have: ${filled.join(', ')}. `;
      }
      if (missing.length > 0) {
        response += t('stillNeedFields', { fields: translateFields(missing).join(', ') }) + '.';;
      } else {
        response += "I have everything I need!";
      }
      
      await speakText(response);
      setStep('followup');
      return true;
    }

    // 5. SPECIFIC FIELD UPDATES
    const fieldUpdates = {
      'change the date': 'due_date',
      'update the date': 'due_date',
      'different date': 'due_date',
      'change the time': 'due_time',
      'update the time': 'due_time',
      'different time': 'due_time',
      'change priority': 'priority',
      'update priority': 'priority',
      'change location': 'location',
      'update location': 'location',
      'change contact': 'contact',
      'update contact': 'contact',
      'change category': 'category',
      'update category': 'category',
      'add notes': 'notes',
      'change notes': 'notes'
    };

    for (const [phrase, field] of Object.entries(fieldUpdates)) {
      if (lowerText.includes(phrase)) {
        const question = generateFollowupQuestion(field, taskPreview);
        await speakText(question);
        
        // Move this field to the front of the missing fields queue
        const updatedMissing = [field, ...currentMissingFields.filter(f => f !== field)];
        setCurrentMissingFields(updatedMissing);
        setStep('followup');
        return true;
      }
    }

    // 6. ENHANCED PROGRESS & STATUS QUESTIONS  
    if (lowerText.includes('what do you need') || lowerText.includes('what else') || 
        lowerText.includes('what more') || lowerText.includes('what\'s left') || 
        lowerText.includes('what remaining') || lowerText.includes('what still need') ||
        // ENHANCED PATTERNS
        lowerText.includes('which information do you need') ||
        lowerText.includes('what information') || lowerText.includes('tell me what you need') ||
        lowerText.includes('what are you missing') || lowerText.includes('what\'s missing') ||
        lowerText.includes('list what you need') || lowerText.includes('show me what\'s left')) {
      
      if (currentMissingFields.length > 0) {
        console.log('🐛 VOICE RESPONSE DEBUG - what do you need:');
        console.log('  - currentMissingFields:', currentMissingFields);
        const remaining = translateFields(currentMissingFields).join(', ');
        const nextField = translateField(currentMissingFields[0]);
        console.log('  - remaining (translated):', remaining);
        console.log('  - nextField (translated):', nextField);
        const finalMessage = t('iStillNeed', { remaining, nextField });
        console.log('  - finalMessage:', finalMessage);
        await speakText(finalMessage);
        
        const question = generateFollowupQuestion(currentMissingFields[0], taskPreview);
        await speakText(question);
      } else {
        await speakText(t('haveEverythingReady'));
      }
      setStep('followup');
      return true;
    }

    // 7. SCHEMA & OPTIONS QUESTIONS
    if (lowerText.includes('category') && (lowerText.includes('options') || lowerText.includes('choices') || 
        lowerText.includes('list') || lowerText.includes('available') || lowerText.includes('what are'))) {
      await speakText(t('availableCategories', { categories: taskSchema.category.options.join(', ') }));
      setStep('followup');
      return true;
    }

    if (lowerText.includes('priority') && (lowerText.includes('levels') || lowerText.includes('options') || 
        lowerText.includes('choices') || lowerText.includes('what are'))) {
      await speakText(t('priorityLevels', { priorities: taskSchema.priority.options.join(', ') }));
      setStep('followup');
      return true;
    }

    // 8. HELP & EXAMPLES
    if (lowerText.includes('help') || lowerText.includes('how do i') || lowerText.includes('example') || 
        lowerText.includes('don\'t understand') || lowerText.includes('confused')) {
      
      if (currentMissingFields.length > 0) {
        const currentField = currentMissingFields[0];
        const fieldInfo = taskSchema[currentField];
        if (fieldInfo?.examples) {
          await speakText(t('fieldExamples', { fieldDescription: fieldInfo.description, examples: fieldInfo.examples.join(' or ') }));
        } else if (fieldInfo?.options) {
          await speakText(t('fieldOptions', { fieldDescription: fieldInfo.description, options: fieldInfo.options.join(', ') }));
        } else {
          await speakText(t('fieldInstructions', { fieldDescription: fieldInfo?.description || currentField.replace(/_/g, ' ') }));
        }
      } else {
        await speakText(t('describeTaskNaturally'));
      }
      setStep('followup');
      return true;
    }

    // 9. ENHANCED NAVIGATION & CONTROL - Including Smart "No Contact" Recognition
    if (lowerText.includes('skip') || lowerText.includes('not needed') || lowerText.includes('no ') || 
        lowerText.includes('none') || lowerText.includes('don\'t need') || lowerText.includes('not required') ||
        lowerText.includes('leave blank') || lowerText.includes('ignore') ||
        // ENHANCED "NO CONTACT" PATTERNS
        lowerText.includes('no contact') || lowerText.includes('don\'t want any contact') ||
        lowerText.includes('i don\'t want you to add any contact') ||
        lowerText.includes('don\'t add a contact') || lowerText.includes('without contact') ||
        lowerText.includes('no person involved') || lowerText.includes('skip contact') ||
        lowerText.includes('ignore contact') || lowerText.includes('continue without')) {
      
      const nextFields = currentMissingFields.slice(1);
      setCurrentMissingFields(nextFields);
      
      if (nextFields.length > 0) {
        const nextQuestion = generateFollowupQuestion(nextFields[0], taskPreview);
        await speakText(t('okaySkipping', { nextQuestion }));
        setStep('followup');
      } else {
        await speakText(t('perfectEnoughInfo'));
        setStep('confirm');
      }
      return true;
    }

    if (lowerText.includes('go back') || lowerText.includes('previous') || lowerText.includes('undo')) {
      await speakText(t('whatToChange'));
      setStep('followup');
      return true;
    }

    if (lowerText.includes('start over') || lowerText.includes('restart') || lowerText.includes('begin again') ||
        lowerText.includes('new task') || lowerText.includes('different task')) {
      await speakText(t('startFreshTask'));
      setStep('initial');
      setTaskPreview(null);
      setCurrentMissingFields([]);
      setTranscript('');
      return true;
    }

    // 10. CONFIRMATION & POSITIVE RESPONSES
    if (lowerText.includes('yes') || lowerText.includes('yeah') || lowerText.includes('correct') ||
        lowerText.includes('right') || lowerText.includes('exactly') || lowerText.includes('that\'s right')) {
      
      // If we're asking about using a found contact
      if (taskPreview?.contact_name && currentMissingFields.includes('contact')) {
        const nextFields = currentMissingFields.filter(f => f !== 'contact');
        setCurrentMissingFields(nextFields);
        
        if (nextFields.length > 0) {
          const nextQuestion = generateFollowupQuestion(nextFields[0], taskPreview);
          await speakText(t('greatUsing', { contactName: taskPreview.contact_name, nextQuestion }));
          setStep('followup');
        } else {
          await speakText(t('perfectReadyToCreate'));
          setStep('confirm');
        }
        return true;
      }
    }

    // 11. NEGATIVE RESPONSES
    if (lowerText.includes('no') || lowerText.includes('nope') || lowerText.includes('not right') ||
        lowerText.includes('incorrect') || lowerText.includes('wrong')) {
      
      if (currentMissingFields.length > 0) {
        const currentField = currentMissingFields[0];
        const question = generateFollowupQuestion(currentField, taskPreview);
        await speakText(t('okayLetMeAskAgain', { question }));
        setStep('followup');
        return true;
      }
    }

    return false; // Not a recognized meta question
  }, [currentMissingFields, taskPreview, generateFollowupQuestion, speakText, handleConversationalAI]);

  // Simplified follow-up response processing
  const processFollowupResponse = useCallback(async (text) => {
    if (!text.trim()) return;
    
    const lowerText = text.toLowerCase().trim();
    
    // Check for create commands - user wants to proceed without more info
    const createTaskCommands = [
      'create the task', 'make the task', 'save the task', 'create it now',
      'go ahead and create', 'create with what you have', 'just create it',
      'that\'s enough', 'proceed', 'create now', 'done', 'skip'
    ];
    
    const isCreateCommand = createTaskCommands.some(command => lowerText.includes(command));
    
    if (isCreateCommand) {
      console.log('🎯 User wants to create task during follow-up:', text);
      
      if (taskPreview && taskPreview.title) {
        await speakText(t('creatingTaskWithInfo'));
        setTimeout(async () => {
          if (createTaskRef.current) {
            await createTaskRef.current();
          }
        }, 1000);
        return;
      } else {
        await speakText(t('needTitleAtLeast'));
        setStep('followup');
        return;
      }
    }
    
    // Simplified processing - mainly for date/time input
    setStep('processing');
    
    try {
      await speakText(t('processingResponse'));
      
      // Parse the response with AI
      const response = await axios.post(`${API}/ai/parse-task`, {
        user_input: text,
        context: {
          current_date: new Date().toISOString().split('T')[0],
          current_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
          existing_data: taskPreview || {},
          language: getLanguageName ? getLanguageName() : "English",
          language_code: getLanguageCode()
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const aiData = response.data.data;
        
        // Merge with existing task data
        const updatedFields = { ...taskPreview, ...aiData.fields };
        
        // Set default priority to "High" if not set
        if (!updatedFields.priority) {
          updatedFields.priority = 'High';
        }
        
        // Smart category selection if not set
        if (!updatedFields.category) {
          updatedFields.category = await smartSelectCategory(updatedFields.title || text);
        }
        
        setTaskPreview(updatedFields);
        
        // Check if we still need date or time
        const stillMissing = [];
        if (!updatedFields.due_date) {
          stillMissing.push('due_date');
        }
        if (!updatedFields.due_time && !updatedFields.time) {
          stillMissing.push('due_time');
        }
        
        if (stillMissing.length > 0) {
          // Still missing required fields, ask for them
          setCurrentMissingFields(stillMissing);
          const nextQuestion = generateFollowupQuestion(stillMissing[0], updatedFields);
          await speakText(nextQuestion);
          setStep('followup');
        } else {
          // Have all required fields, create task
          await speakText(t('creatingTaskWithInfo'));
          setTimeout(async () => {
            if (createTaskRef.current) {
              await createTaskRef.current();
            }
          }, 1000);
        }
        
      } else {
        // AI parsing failed, ask user to try again
        await speakText(t('didntCatchRepeat'));
        setStep('followup');
      }
      
    } catch (error) {
      console.error('❌ Follow-up processing error:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = "Sorry, I had trouble processing that response. ";
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage += "Please make sure you're logged in and try again.";
      } else if (error.response?.status >= 500) {
        errorMessage += "There's a server issue. Please try again in a moment.";
      } else {
        errorMessage += "Could you try saying that differently?";
      }
      
      await speakText(errorMessage);
      setStep('followup');
    }
  }, [taskPreview, smartSelectCategory, t, speakText, getLanguageName, getLanguageCode]);

  // Process the transcript with AI (moved before startListening to fix temporal dead zone)
  const processTranscript = useCallback(async (text) => {
    if (!text.trim()) return;
    
    setStep('processing');
    
    try {
      await speakText(t('letMeProcess'));
      
      const response = await axios.post(`${API}/ai/parse-task`, {
        user_input: text,
        context: {
          current_date: new Date().toISOString().split('T')[0],
          current_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
          language: getLanguageName ? getLanguageName() : "English",
          language_code: getLanguageCode()
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const aiData = response.data.data;
        const fields = aiData.fields || {};
        
        // Simplified field handling - only core fields needed
        console.log('🔄 Task fields:', fields);
        
        // Set defaults for missing core required fields
        // Priority defaults to "High" if not mentioned by user
        if (!fields.priority) {
          fields.priority = 'High';
        }
        
        // Smart category selection if not mentioned by user
        if (!fields.category) {
          fields.category = await smartSelectCategory(fields.title || text);
        }
        
        // Check for required fields: date and time
        const missingFields = [];
        if (!fields.due_date) {
          missingFields.push('due_date');
        }
        if (!fields.due_time && !fields.time) {
          missingFields.push('due_time');
        }
        
        const uniqueMissing = missingFields;
        
        setCurrentMissingFields(uniqueMissing);
        setTaskPreview(fields);
        
        if (fields.title) {
          // We have a title, check if we need to ask for date/time
          if (uniqueMissing.length === 0) {
            // Have all required fields, create task immediately
            await speakText(t('perfectEnoughInfo'));
            
            setTimeout(async () => {
              if (createTaskRef.current) {
                await createTaskRef.current();
              }
            }, 1000);
            
          } else {
            // Ask for missing required fields (date and/or time)
            const nextField = uniqueMissing[0];
            const question = generateFollowupQuestion(nextField, fields);
            await speakText(question);
            setStep('followup');
          }
        } else {
          // No title, ask for clarification
          await speakText(t('needTitleAtLeast'));
          setStep('followup');
        }
        
      } else {
        await speakText(t('couldntUnderstandTask'));
        setStep('listening');
        // Will auto-start listening when step changes to 'listening'
      }
      
    } catch (error) {
      console.error('❌ Process transcript error:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = "Sorry, I had trouble processing that. ";
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage += "Please make sure you're logged in and try again.";
      } else if (error.response?.status >= 500) {
        errorMessage += "There's a server issue. Please try again in a moment.";
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage += "Please check your internet connection and try again.";
      } else {
        errorMessage += "Please try describing your task differently, or use the text input option.";
      }
      
      await speakText(errorMessage);
      setStep('listening');
      // Will auto-start listening when step changes to 'listening'
    }
  }, [speakText, generateFollowupQuestion]);

  // ---------- Whisper fallback (for iOS Safari / browsers without Web Speech) ----------
  // Records a short audio clip with MediaRecorder, uploads it to /api/voice/transcribe,
  // and feeds the resulting text through the same downstream flow as Web Speech does.
  const whisperMediaRecorderRef = useRef(null);
  const whisperChunksRef = useRef([]);
  const whisperStreamRef = useRef(null);
  const whisperStopTimeoutRef = useRef(null);

  const stopWhisperRecording = useCallback(() => {
    if (whisperMediaRecorderRef.current && whisperMediaRecorderRef.current.state !== 'inactive') {
      try { whisperMediaRecorderRef.current.stop(); } catch (e) { /* noop */ }
    }
    if (whisperStreamRef.current) {
      whisperStreamRef.current.getTracks().forEach((t) => t.stop());
      whisperStreamRef.current = null;
    }
    if (whisperStopTimeoutRef.current) {
      clearTimeout(whisperStopTimeoutRef.current);
      whisperStopTimeoutRef.current = null;
    }
  }, []);

  const startListeningWhisper = useCallback(async () => {
    console.log('🎤 [Whisper] starting MediaRecorder fallback');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      await speakText(t('voiceNotSupported'));
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('❌ [Whisper] mic permission denied:', err);
      await speakText(t('microphoneBlocked'));
      return;
    }
    whisperStreamRef.current = stream;

    // Pick a mime type the browser supports for MediaRecorder
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    let mimeType = '';
    for (const c of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
        mimeType = c;
        break;
      }
    }

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    whisperMediaRecorderRef.current = recorder;
    whisperChunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) whisperChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      setIsListening(false);
      const blob = new Blob(whisperChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      console.log(`🎤 [Whisper] captured ${blob.size} bytes (${blob.type})`);
      if (!blob.size) {
        await speakText(t('voiceCouldntStart') || "I couldn't hear anything. Please try again.");
        return;
      }

      const formData = new FormData();
      const ext = (blob.type.includes('mp4') || blob.type.includes('m4a')) ? 'm4a'
        : (blob.type.includes('ogg') ? 'ogg' : 'webm');
      formData.append('file', blob, `voice.${ext}`);
      const langCode = getLanguageCode ? getLanguageCode() : 'en';
      formData.append('language_code', langCode);

      try {
        const response = await axios.post(`${API}/voice/transcribe`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          timeout: 60000,
        });
        const text = (response.data?.text || '').trim();
        console.log('✅ [Whisper] transcript:', text);
        if (!text) {
          await speakText("I couldn't catch that. Please try again.");
          return;
        }
        setTranscript(text);
        // Route through the same downstream path as Web Speech.
        if (step === 'followup') {
          processFollowupResponse(text);
        } else {
          processTranscript(text);
        }
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        console.error('❌ [Whisper] transcription request failed:', status, detail || err.message);
        if (status === 402) {
          await speakText("Voice tasks are a Pro feature. Please upgrade to use voice input.");
        } else {
          await speakText("Couldn't transcribe your voice. Please type your task or try again.");
        }
      }
    };

    recorder.start();
    setIsListening(true);
    // Auto-stop after 30s as a safety net to avoid runaway recordings.
    whisperStopTimeoutRef.current = setTimeout(() => {
      console.log('⏰ [Whisper] auto-stopping after 30s');
      stopWhisperRecording();
    }, 30000);
  }, [speakText, t, getLanguageCode, step, processFollowupResponse, processTranscript, stopWhisperRecording]);

  // Cleanup MediaRecorder on unmount
  useEffect(() => {
    return () => stopWhisperRecording();
  }, [stopWhisperRecording]);

  // Enhanced speech recognition with proper timeout and pause detection
  const startListening = useCallback(async () => {
    console.log('🎤 === Starting Speech Recognition Debug ===');
    
    // Check browser support first
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('⚠️ Web Speech API not supported — using Whisper fallback');
      return startListeningWhisper();
    }

    // Check if we're on HTTPS or localhost (required for microphone access)
    const isSecureContext = window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    console.log('🔒 Secure context (HTTPS/localhost):', isSecureContext);
    
    if (!isSecureContext) {
      console.error('❌ Microphone requires HTTPS or localhost');
      await speakText(t('needsHTTPS'));
      return;
    }

    // Check microphone permissions without requesting immediately (let Web Speech API handle it)
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        console.log('🎤 Current microphone permission state:', result.state);
        
        if (result.state === 'denied') {
          console.error('❌ Microphone permission denied');
          await speakText(t('microphoneBlocked'));
          return;
        }
      }
    } catch (permError) {
      console.log('⚠️ Could not check microphone permissions, proceeding with speech recognition:', permError);
    }

    // Stop any existing recognition first
    if (recognitionRef.current) {
      console.log('🎤 Stopping existing recognition');
      recognitionRef.current.abort();
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    // Get current language and map to speech recognition language codes
    const currentLanguageCode = getLanguageCode ? getLanguageCode() : 'en';
    const speechRecognitionLanguages = {
      'en': 'en-US',
      'es': 'es-ES',
      'fr': 'fr-FR'
    };
    const recognitionLang = speechRecognitionLanguages[currentLanguageCode] || 'en-US';
    
    console.log('🌍 Setting speech recognition language to:', recognitionLang, 'for user language:', currentLanguageCode);
    
    // Enhanced configuration for better reliability
    recognition.continuous = false;  // Stop after user finishes speaking
    recognition.interimResults = false;
    recognition.lang = recognitionLang;  // Use user's selected language
    recognition.maxAlternatives = 1;
    
    // Add timeout to prevent infinite listening
    let recognitionTimeout;
    let hasStarted = false;
    
    recognition.onstart = () => {
      console.log('✅ Speech recognition started successfully');
      hasStarted = true;
      setIsListening(true);
      
      // Set timeout to automatically stop after 30 seconds
      recognitionTimeout = setTimeout(() => {
        console.log('⏰ Recognition timeout (30s) - stopping automatically');
        recognition.abort();
        setIsListening(false);
      }, 30000); // 30 second timeout
    };

    recognition.onresult = (event) => {
      const result = event.results[0][0].transcript;
      const confidence = event.results[0][0].confidence;
      console.log('✅ Speech recognized:', result, 'Confidence:', confidence);
      
      setTranscript(result);
      setIsListening(false);
      
      // Clear timeout since we got a result
      if (recognitionTimeout) {
        clearTimeout(recognitionTimeout);
      }
      
      // Process based on current step
      setTimeout(() => {
        if (step === 'followup') {
          processFollowupResponse(result);
        } else {
          processTranscript(result);
        }
      }, 500);
    };

    recognition.onerror = async (event) => {
      console.error('❌ Speech recognition error:', event.error);
      setIsListening(false);
      
      // Clear timeout on error
      if (recognitionTimeout) {
        clearTimeout(recognitionTimeout);
      }
      
      // Handle different types of errors with specific user feedback
      let errorMessage = '';
      switch (event.error) {
        case 'no-speech':
          errorMessage = "I didn't hear anything. Please try speaking clearly or use the text input option.";
          console.log('⚠️ No speech detected');
          break;
        case 'audio-capture':
          errorMessage = "I can't access your microphone. Please check your browser permissions and try again, or use the text input option.";
          console.error('❌ Microphone access denied or unavailable');
          break;
        case 'not-allowed':
          errorMessage = "Microphone permission was denied. Please allow microphone access in your browser and try again, or use the text input option.";
          console.error('❌ Microphone permission explicitly denied');
          break;
        case 'network':
          errorMessage = "There was a network error. Please check your connection and try again, or use the text input option.";
          console.error('❌ Network error during speech recognition');
          break;
        case 'aborted':
          console.log('ℹ️ Speech recognition was stopped by user');
          return; // Don't show error message for user-initiated stops
        default:
          errorMessage = `Voice recognition error: ${event.error}. Please try again or use the text input option.`;
          console.error(`❌ Unknown speech error: ${event.error}`);
      }

      if (errorMessage) {
        await speakText(errorMessage);
      }
    };

    recognition.onend = () => {
      console.log('ℹ️ Speech recognition ended');
      setIsListening(false);
      
      // Clear timeout when recognition ends normally
      if (recognitionTimeout) {
        clearTimeout(recognitionTimeout);
      }
    };

    // Store recognition reference for cleanup
    recognitionRef.current = recognition;
    
    try {
      console.log('🎤 Attempting to start speech recognition...');
      console.log('🎤 Recognition object created:', !!recognition);
      console.log('🎤 Recognition continuous:', recognition.continuous);
      console.log('🎤 Recognition language:', recognition.lang);
      
      recognition.start();
      console.log('🎤 Recognition.start() called successfully');
      
      // Give it a moment to start, then check if it actually started
      setTimeout(() => {
        if (!hasStarted) {
          console.error('❌ Speech recognition failed to start within 2 seconds');
          console.error('❌ This could be due to:');
          console.error('   - Microphone not found or accessible');
          console.error('   - Browser blocking microphone access');
          console.error('   - Another application using microphone');
          console.error('   - Browser not supporting speech recognition');
          setIsListening(false);
          speakText(t('voiceCouldntStart'));
        }
      }, 3000); // Increased to 3 seconds
      
    } catch (error) {
      console.error('❌ Failed to start speech recognition:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      setIsListening(false);
      
      if (recognitionTimeout) {
        clearTimeout(recognitionTimeout);
      }
      
      let errorMessage = "Voice recognition couldn't start. ";
      if (error.name === 'InvalidStateError') {
        errorMessage += "Speech recognition is already active. Please try again.";
      } else if (error.name === 'NotSupportedError') {
        errorMessage += "Your browser doesn't support voice recognition. Please use the text input option.";
      } else {
        errorMessage += "Please check your microphone and try again, or use the text input option.";
      }
      
      await speakText(errorMessage);
    }
  }, [step, processFollowupResponse, processTranscript, speakText]);

  // Store startListening in ref to avoid temporal dead zone issues
  React.useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  // Stop listening function
  const stopListening = useCallback(async () => {
    console.log('🛑 Manually stopping speech recognition');
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
        console.log('✅ Speech recognition stopped successfully');
      } catch (error) {
        console.error('❌ Error stopping speech recognition:', error);
      }
    }
    // Also stop the Whisper MediaRecorder fallback if it is currently active.
    stopWhisperRecording();
    
    setIsListening(false);
    await speakText(t('listeningStopped'));
  }, [speakText, t, stopWhisperRecording]);

  // Create the actual task
  const createTask = useCallback(async () => {
    if (!taskPreview) return;
    
    setStep('processing');
    
    try {
      await speakText(t('createTaskNow'));
      
      const taskData = {
        title: taskPreview.title || 'Voice-created task',
        due_date: taskPreview.due_date ? new Date(taskPreview.due_date).toISOString() : null,
        time: taskPreview.due_time || taskPreview.time,
        category: taskPreview.category || '',
        priority: taskPreview.priority || 'Medium'
        // Removed: location, notes - keeping it simple as requested
      };

      const response = await axios.post(`${API}/tasks`, taskData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      await speakText(t('taskCreatedSuccess'));
      
      if (onTaskCreated) {
        onTaskCreated(response.data);
      }
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Create task error:', error);
      await speakText(t('taskCreationFailed'));
    }
  }, [taskPreview, speakText, onTaskCreated, onClose]);

  // Store createTask in ref to avoid temporal dead zone issues
  React.useEffect(() => {
    createTaskRef.current = createTask;
  }, [createTask]);

  // Start the conversation
  const startConversation = useCallback(async () => {
    try {
      setStep('speaking');
      console.log('🎤 Starting conversation...');
      
      // Try to speak the welcome message
      const success = await speakText(t('welcomeMessage'));
      
      if (success) {
        console.log('✅ TTS introduction completed');
      } else {
        console.log('⚠️ TTS not available, continuing with voice input only');
      }
      
      // Always proceed to listening regardless of TTS success
      setStep('listening');
      // Will auto-start listening when step changes to 'listening'
      
    } catch (error) {
      console.error('❌ Conversation start error:', error);
      // Still proceed to listening even if there's an error
      setStep('listening');
      // Will auto-start listening when step changes to 'listening'
    }
  }, [speakText]);

  // Auto-start listening when step changes to followup or listening. We use a
  // very short delay (150 ms) — just enough for the modal's mount animation
  // to settle so the mic-permission prompt doesn't visually clash with the
  // dialog fly-in. Previously this was 1 s which felt sluggish.
  React.useEffect(() => {
    if ((step === 'followup' || step === 'listening') && !isListening) {
      const timer = setTimeout(() => {
        if (startListeningRef.current) {
          startListeningRef.current();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [step, isListening]); // Removed startListening from dependencies

  // Cleanup speech recognition on unmount
  React.useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Manual text input fallback (state moved to top)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <img 
              src="/logos/edotask-icon.png" 
              alt="Edotask" 
              className="w-8 h-8 mr-3"
            />
            <h3 className="text-xl font-bold text-gray-900">{t('aiTaskCreator')}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Status Display */}
        <div className="text-center mb-6">
          {/* Initial step removed — the modal opens directly in 'listening'
              mode so the user can speak as soon as the dialog appears. */}

          {step === 'speaking' && (
            <div>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <span className="text-2xl">🗣️</span>
              </div>
              <h4 className="text-lg font-semibold text-blue-600">AI Speaking...</h4>
              <p className="text-sm text-gray-600">Please listen</p>
            </div>
          )}

          {step === 'listening' && (
            <div>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <span className="text-2xl">🎤</span>
              </div>
              <h4 className="text-lg font-semibold text-red-600">I'm listening</h4>
              <p className="text-sm text-gray-600">Say your task — I'll stop when you pause.</p>
              {transcript && (
                <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                  "{transcript}"
                </div>
              )}
            </div>
          )}

          {step === 'processing' && (
            <div>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h4 className="text-lg font-semibold text-yellow-600">Processing...</h4>
              <p className="text-sm text-gray-600">Analyzing your request</p>
            </div>
          )}

          {step === 'followup' && (
            <div>
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <span className="text-2xl">❓</span>
              </div>
              <h4 className="text-lg font-semibold text-orange-600">{t('needMoreInfo')}</h4>
              <p className="text-sm text-gray-600">{t('pleaseProvideRequested')}</p>
              {transcript && (
                <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                  "{transcript}"
                </div>
              )}
              {taskPreview && (
                <div className="mt-3 p-2 bg-gray-50 rounded-lg text-left text-xs">
                  <p className="font-medium">{t('taskProgress')}</p>
                  {taskPreview.title && <p>✅ Title: {taskPreview.title}</p>}
                  {taskPreview.due_date ? (
                    <p>✅ Date: {new Date(taskPreview.due_date).toLocaleDateString()}</p>
                  ) : (
                    <p>❓ Date: Not set</p>
                  )}
                  {taskPreview.due_time ? (
                    <p>✅ Time: {taskPreview.due_time}</p>
                  ) : (
                    <p>❓ Time: Not set</p>
                  )}
                  {taskPreview.priority ? (
                    <p>✅ Priority: {taskPreview.priority}</p>
                  ) : (
                    <p>❓ Priority: Not set</p>
                  )}
                  {taskPreview.category ? (
                    <p>✅ Category: {taskPreview.category}</p>
                  ) : (
                    <p>❓ Category: Auto-selecting...</p>
                  )}
                </div>
              )}
              {currentMissingFields.length > 0 && (
                <div className="mt-2 p-2 bg-orange-50 rounded text-xs">
                  <p className="font-medium text-orange-700">
                    {(() => {
                      console.log('🐛 MODAL RENDER DEBUG:');
                      console.log('  - currentMissingFields:', currentMissingFields);
                      console.log('  - getLanguageCode():', getLanguageCode());
                      
                      const translatedFields = translateFields(currentMissingFields);
                      console.log('  - translatedFields:', translatedFields);
                      
                      const fieldsText = translatedFields.join(', ');
                      console.log('  - fieldsText:', fieldsText);
                      
                      const finalMessage = t('stillNeedFields', { fields: fieldsText });
                      console.log('  - finalMessage:', finalMessage);
                      
                      return finalMessage;
                    })()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Confirm display removed - auto-creation flow */}
        </div>

        {/* Text Input Fallback - REMOVED */}

        {/* Control Buttons */}
        <div className="flex gap-3">
          {/* Initial "Start Voice" button removed — mic auto-starts now. */}

          {(step === 'listening' || step === 'followup') && isListening && (
            <button
              onClick={stopListening}
              className="flex-1 py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
            >
              ⏹️ Stop Listening
            </button>
          )}

          {step === 'followup' && !isListening && (
            <>
              <button
                onClick={() => startListening()}
                className="flex-1 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
              >
                🎤 {t('continueAnswering')}
              </button>
            </>
          )}

          {/* Confirm step removed - tasks are auto-created now */}

          <button
            onClick={onClose}
            className="px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            {t('cancel')}
          </button>
        </div>

        {/* Help text */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            💡 {t('tryExample')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimpleVoiceTaskCreator;